
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Star, MessageCircle, Users, AlertCircle, Edit } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Review } from '@/hooks/useReviews';
import ReviewList from '../ReviewList';
import ReviewForm from '../ReviewForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

interface ReviewsCardProps {
  businessId: string;
  reviews: Review[];
  isLoading: boolean;
  error: Error | null;
  averageRating: number;
  reviewCount: number;
  isBusinessOwner: boolean;
  userReview: Review | null;
  createReview?: (businessId: string, rating: number, description: string) => Promise<void>;
  updateReview?: (reviewId: string, rating: number, description: string) => Promise<void>;
  deleteReview?: (reviewId: string) => Promise<void>;
  isAuthenticated: boolean;
}

const ReviewsCard: React.FC<ReviewsCardProps> = ({
  businessId,
  reviews,
  isLoading,
  error,
  averageRating,
  reviewCount,
  isBusinessOwner,
  userReview,
  createReview,
  updateReview,
  deleteReview,
  isAuthenticated
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const formattedRating = typeof averageRating === 'number' ? averageRating.toFixed(1) : '0.0';
  
  // Check for showReviewForm URL parameter after auth redirect
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('showReviewForm') === 'true' && user && !isBusinessOwner) {
      setShowReviewDialog(true);
    }
  }, [location.search, user, isBusinessOwner]);
  
  const handleSubmitReview = async (values: { rating: number; description: string }) => {
    setSubmitError(null);
    setIsSubmitting(true);
    
    try {
      if (!user) {
        setSubmitError(t('service.details.review_login_required', 'Please sign in to write a review'));
        return;
      }
      
      if (userReview && updateReview) {
        await updateReview(userReview.id, values.rating, values.description);
      } else if (createReview) {
        await createReview(businessId, values.rating, values.description);
      }
      
      setShowReviewDialog(false);
    } catch (err: any) {
      console.error("Review submission error:", err);
      setSubmitError(err.message || t('service.details.review_submit_error', 'Failed to submit review'));
      toast({
        variant: "destructive",
        title: userReview ? t('service.details.review_update_error', 'Failed to update review') : t('service.details.review_create_error', 'Failed to create review'),
        description: err.message || t('service.details.review_problem', 'There was a problem with your review')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddReview = () => {
    if (!isAuthenticated) {
      const returnUrl = `${window.location.pathname}?showReviewForm=true`;
      navigate(`/auth?returnTo=${encodeURIComponent(returnUrl)}`);
    } else if (!isBusinessOwner) {
      setShowReviewDialog(true);
    }
  };

  const renderEmptyState = () => (
    <div className="text-center py-12 space-y-6">
      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center">
        <Users className="w-10 h-10 text-primary" />
      </div>
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-gray-900">
          {t('service.details.no_reviews_title', 'Be the first to share your experience!')}
        </h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
          {t('service.details.no_reviews_description', 'Your review helps other community members discover great services. Share your honest feedback and help this business grow.')}
        </p>
      </div>
      {!isBusinessOwner && (
        <div className="pt-2">
          {isAuthenticated ? (
            <button 
              onClick={() => setShowReviewDialog(true)}
              className="text-primary hover:text-primary/80 hover:underline transition-colors inline-flex items-center gap-2 font-medium"
            >
              <Star className="w-4 h-4" />
              {t('service.details.write_first_review', 'Write the First Review')}
            </button>
          ) : (
            <button 
              onClick={handleAddReview}
              className="text-primary hover:text-primary/80 hover:underline transition-colors inline-flex items-center gap-2 font-medium"
            >
              <MessageCircle className="w-4 h-4" />
              {t('service.details.sign_in_to_review', 'Sign In to Review')}
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderReviewsHeader = () => (
    <div className="mb-6">
      {/* Title and rating on same line */}
      <div className="flex items-center icon-heading mb-1">
        <h2 className="text-lg font-semibold text-gray-900 leading-none">
          {t('service.details.reviews_title')}
        </h2>
        {reviewCount > 0 ? (
          <div className="flex items-center gap-1 ml-2">
            <Star className="w-4 h-4 text-yellow-400 fill-current" />
            <span className="font-medium text-gray-900">{formattedRating}</span>
            <span className="text-sm text-muted-foreground">
              ({reviewCount === 1 
                ? t('service.details.one_review', '1 review')
                : t('service.details.multiple_reviews', `${reviewCount} reviews`)
              })
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground ml-2">{t('service.card.no_reviews')}</span>
        )}
      </div>
      
      {/* Review action links below title with minimal spacing */}
      <div className="flex justify-start">
        {!isBusinessOwner && !userReview && isAuthenticated && (
          <button 
            onClick={() => setShowReviewDialog(true)}
            className="text-xs text-primary hover:text-primary/80 hover:underline transition-colors"
          >
            {t('service.details.write_review')}
          </button>
        )}
        
        {!isBusinessOwner && userReview && isAuthenticated && (
          <button 
            onClick={() => setShowReviewDialog(true)}
            className="text-xs text-primary hover:text-primary/80 hover:underline transition-colors flex items-center gap-1"
          >
            <Edit className="w-3 h-3" />
            {t('service.details.edit_review')}
          </button>
        )}
        
        {!isAuthenticated && !isBusinessOwner && (
          <button 
            onClick={handleAddReview}
            className="text-xs text-primary hover:text-primary/80 hover:underline transition-colors flex items-center gap-1"
          >
            <MessageCircle className="w-3 h-3" />
            {t('service.details.sign_in_to_review', 'Sign In to Review')}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <Card className="bg-gradient-to-br from-white to-gray-50/30 border shadow-sm hover:shadow-md transition-all duration-300">
        <CardContent className="p-6">
          {reviewCount === 0 ? (
            renderEmptyState()
          ) : (
            <div>
              {renderReviewsHeader()}
              <ReviewList 
                reviews={reviews}
                isLoading={isLoading}
                error={error}
                showLoadMore={true}
                onDeleteReview={deleteReview}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog 
        open={showReviewDialog} 
        onOpenChange={(open) => {
          setShowReviewDialog(open);
          if (!open) setSubmitError(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {userReview ? t('service.details.edit_review') : t('service.details.write_review')}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {submitError && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{submitError}</AlertDescription>
              </Alert>
            )}
            <ReviewForm
              businessId={businessId}
              existingReview={userReview}
              onSubmit={handleSubmitReview}
              isSubmitting={isSubmitting}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReviewsCard;
